import {
  Outlet,
  isRouteErrorResponse,
  useLoaderData,
  useNavigation,
  useRouteError,
} from '@remix-run/react'
import type {
  MetaFunction,
  LoaderFunctionArgs,
  HeadersFunction,
} from '@remix-run/node'
import { json } from '@remix-run/node'
import { Box } from '@n26/nxd'

import { useState } from 'react'
import { askNotToTrackCookie } from '@app/cookies.server'
import { companyName } from '@constants/seo'
import { TranslatorContext } from '@contexts/TranslatorContext'
import { GlobalClientConfigContext } from '@contexts/GlobalClientConfigContext'
import { Header, MinimalHeader } from '@components/Header'
import { Footer } from '@components/Footer'
import {
  PageVariationsContext,
  defaultPageVariations,
} from '@contexts/PageVariationsContext'
import type { UrlsPerCountry } from '@constants/countryDetails'
import { APP_LOCALES, type AppLocales } from '@constants/i18n'
import { arrayIncludes } from '@helpers/arrayIncludes'
import { getHeaderData } from '@helpers/getHeaderData'
import { getFooter } from '@helpers/getFooter'
import { getPreviewFromFetchRequest } from '@helpers/getPreviewFromFetchRequest'
import { NotFoundPage } from '@pages/NotFound'
import { OneTrustCookieNote } from '@components/OneTrustCookieNote'
import { config } from '@infrastructure/config'
import { useLandingPageData } from '@hooks/useLandingPageData'
import type { HeaderFooterMode } from '@helpers/getHeaderFooterMode'
import { getHeaderFooterMode } from '@helpers/getHeaderFooterMode'
import { logger } from '@helpers/core/logger'
import { getTranslationsFromCms } from '@cms/data/getTranslationsFromCms'
import { getCacheControlHeader } from '@helpers/getCacheControlHeader'

export const meta: MetaFunction = () => {
  return [{ title: companyName }]
}

export function ErrorBoundary() {
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    return <NotFoundPage />
  }
}

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { locale } = params
  const { bankingUrl, registrationUrl } = config

  if (locale === undefined || !arrayIncludes(APP_LOCALES, locale)) {
    const cacheControl = getCacheControlHeader({ request, responseStatus: 404 })

    throw new Response(null, {
      status: 404,
      statusText: 'Not Found',
      headers: {
        ...cacheControl,
      },
    })
  } else {
    try {
      const headerData = await getHeaderData(params, request)
      const cookieHeader = request.headers.get('Cookie')
      const askNotToTrackRequestCookie =
        await askNotToTrackCookie.parse(cookieHeader)

      const askNotToTrack = askNotToTrackRequestCookie?.askNotToTrack

      const preview = getPreviewFromFetchRequest(request)
      const footerData = await getFooter(locale, preview)
      const translations = await getTranslations(locale)

      const cacheControl = getCacheControlHeader({ request })

      return json(
        {
          translations,
          headerData,
          footerData,
          bankingUrl,
          registrationUrl,
          askNotToTrack,
        },
        {
          headers: {
            ...cacheControl,
          },
        }
      )
    } catch (error) {
      logger.error('LOCALE_LOADER_ERROR', {
        error,
        locale,
      })

      throw new Response(null, {
        status: 500,
        statusText: 'Internal Server Error',
      })
    }
  }
}

const getTranslations = async (locale: AppLocales) => {
  try {
    return getTranslationsFromCms(locale)
  } catch (error) {
    logger.error('FETCHING_TRANSLATIONS_ERRROR', {
      error,
      locale,
    })
    throw error
  }
}

export type Translations = Record<string, string>

export const headers: HeadersFunction = ({ loaderHeaders, errorHeaders }) => ({
  'Cache-Control':
    errorHeaders?.get('Cache-Control') ||
    loaderHeaders.get('Cache-Control') ||
    'no-store',
})

export type LocaleLoader = typeof loader
type LocaleUnwrappedLoaderData = UnwrapLoaderData<LocaleLoader>

export default function Index() {
  const {
    translations,
    headerData,
    footerData,
    bankingUrl,
    registrationUrl,
    askNotToTrack,
  } = useLoaderData<LocaleLoader>() as LocaleUnwrappedLoaderData
  const navigation = useNavigation()
  const loading = navigation.state !== 'idle'

  const [localizedUrls, setLocalizedUrls] = useState<UrlsPerCountry>(
    defaultPageVariations
  )

  const loadingClassName = loading ? 'opacity-70' : ''

  const landingPageData = useLandingPageData()

  const {
    ctaLink: pageCtaLink,
    qrCodeCta,
    headerFooterMode,
    banner,
    trackingName,
    productPreference,
  } = landingPageData || {}

  const layoutMode = getHeaderFooterMode(headerFooterMode as HeaderFooterMode)

  return (
    <GlobalClientConfigContext.Provider value={{ bankingUrl, registrationUrl }}>
      <TranslatorContext.Provider
        value={{
          loading,
          translations,
        }}
      >
        <OneTrustCookieNote askNotToTrack={askNotToTrack}>
          <PageVariationsContext.Provider
            value={{ localizedUrls, setLocalizedUrls }}
          >
            <Box
              display='flex'
              flexDirection='column'
              width='full'
              height='full'
              className='max-w-full mx-auto'
            >
              {layoutMode?.isMinimalHeader ? (
                <MinimalHeader banner={banner} />
              ) : (
                <Header
                  headerData={headerData}
                  bankingUrl={bankingUrl}
                  registrationUrl={registrationUrl}
                  pageCtaLink={pageCtaLink}
                  qrCodeCta={qrCodeCta}
                  banner={banner}
                  trackingName={trackingName}
                  productPreference={productPreference}
                />
              )}

              <main id='content' role='main'>
                <div className={loadingClassName}>
                  <Outlet />
                </div>
              </main>
              <Footer
                data={footerData}
                showFooterNavigation={layoutMode?.showFooter}
              />
            </Box>
          </PageVariationsContext.Provider>
        </OneTrustCookieNote>
      </TranslatorContext.Provider>
    </GlobalClientConfigContext.Provider>
  )
}
